import { clsx } from 'clsx';
import { cardInfoForDesignContext } from '@tkpod/shared';
import type { DesignContext } from '@tkpod/shared';

interface ProductCardProps {
  name: string;
  slug: string;
  brand: string | null;
  modelName: string | null;
  thumbnailUrl: string | null;
  optionSummary: string | null;
  startingPrice: string | null;
  type: string;
  /**
   * UPT design surface. Drives the footer caption explaining how the product
   * is designed (fixed product design vs. buyer-personalized storefront).
   */
  designContext?: DesignContext | string | null;
  onClick: () => void;
}

export function ProductCard({
  name,
  brand,
  modelName,
  thumbnailUrl,
  optionSummary,
  startingPrice,
  designContext,
  onClick,
}: ProductCardProps) {
  const designInfo = cardInfoForDesignContext(
    designContext as DesignContext | null | undefined,
  );
  return (
    <button
      type="button"
      className={clsx(
        'group w-full rounded-xl border border-border bg-white text-left',
        'transition-all duration-200 hover:shadow-md hover:border-brand-300',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2',
      )}
      onClick={onClick}
    >
      <div className="aspect-square w-full overflow-hidden rounded-t-xl bg-surface-secondary">
        {thumbnailUrl ? (
          <img
            src={thumbnailUrl}
            alt={name}
            className="h-full w-full object-contain p-4 transition-transform duration-200 group-hover:scale-105"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center">
            <svg
              className="h-16 w-16 text-text-tertiary/40"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={0.8}
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="m2.25 15.75 5.159-5.159a2.25 2.25 0 0 1 3.182 0l5.159 5.159m-1.5-1.5 1.409-1.409a2.25 2.25 0 0 1 3.182 0l2.909 2.909M3.75 21h16.5A2.25 2.25 0 0 0 22.5 18.75V5.25A2.25 2.25 0 0 0 20.25 3H3.75A2.25 2.25 0 0 0 1.5 5.25v13.5A2.25 2.25 0 0 0 3.75 21Z"
              />
            </svg>
          </div>
        )}
      </div>

      <div className="p-4">
        {brand && (
          <p className="text-xs font-medium uppercase tracking-wider text-text-tertiary">
            {brand}
          </p>
        )}
        <h3 className="mt-0.5 text-sm font-semibold text-text-primary line-clamp-2 leading-snug">
          {modelName ? `${modelName} ${name}` : name}
        </h3>

        {optionSummary && (
          <p className="mt-2 text-xs text-text-secondary">{optionSummary}</p>
        )}

        {startingPrice && (
          <p className="mt-1.5 text-sm font-semibold text-text-primary">
            Starting at ${parseFloat(startingPrice).toFixed(2)}
          </p>
        )}

        <div className="mt-3">
          <span
            className={clsx(
              'inline-flex items-center gap-1 text-xs font-medium text-brand-600',
              'group-hover:text-brand-700 transition-colors',
            )}
          >
            View Product
            <svg className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
            </svg>
          </span>
        </div>

        <p className="mt-3 border-t border-border pt-3 text-xs text-text-tertiary">
          {designInfo}
        </p>
      </div>
    </button>
  );
}
